import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/api/axios';
import { ENDPOINTS } from '@/api/endpoints';

export interface Organization {
  organizationId: string;
  name: string;
  slug: string;
  workspaceCount: number;
  memberCount: number;
}

interface OrgState {
  orgs: Organization[];
  currentOrgId: string | null;
  isLoading: boolean;
}

interface OrgActions {
  fetchOrgs: () => Promise<void>;
  setCurrentOrgId: (id: string) => void;
  addOrg: (org: Organization) => void;
  clearOrgs: () => void;
}

export const useOrgStore = create<OrgState & OrgActions>()(
  persist(
    (set, get) => ({
  orgs: [],
  currentOrgId: null,
  isLoading: false,

  fetchOrgs: async () => {
    set({ isLoading: true });
    try {
      const r = await api.get(ENDPOINTS.ORGANIZATIONS.MY);
      const orgList: Organization[] = r.data || [];
      const current = get().currentOrgId;
      set({
        orgs: orgList,
        isLoading: false,
        currentOrgId: current && orgList.find(o => o.organizationId === current)
          ? current
          : orgList[0]?.organizationId ?? null,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  setCurrentOrgId: (id) => set({ currentOrgId: id }),

  addOrg: (org) => set((state) => ({
    orgs: [...state.orgs, org],
    currentOrgId: state.currentOrgId ?? org.organizationId,
  })),

  clearOrgs: () => set({ orgs: [], currentOrgId: null }),
    }),
    {
      name: 'org-storage',
      // Persist only the selected org so it survives reloads; the org list is
      // always refetched from the server (and currentOrgId is re-validated there).
      partialize: (state) => ({ currentOrgId: state.currentOrgId }),
    }
  )
);

export const useOrgs = () => useOrgStore((s) => s.orgs);
export const useCurrentOrg = () => useOrgStore((s) => s.orgs.find(o => o.organizationId === s.currentOrgId) ?? null);
